import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { PrescriptionFileStatus } from '@typesApp/PrescriptionFile'
import { ViewName } from '@typesApp/ViewNavigation'
import { config } from '@config/config'

// Note: same claim record status may resume to different view based on specific qualifiers.
// These qualifiers are referenced in the view navigation resumeMapping configuration.
const QUALIFIERS = {
  VALIDATE_PRESCRIPTION_IN_PROGRESS: ':VALIDATE_PRESCRIPTION_IN_PROGRESS',
  HAS_REFERRER_NO_PRESCRIPTION: ':HAS_REFERRER_NO_PRESCRIPTION',
  HAS_REFERRER_WITH_PRESCRIPTION: ':HAS_REFERRER_WITH_PRESCRIPTION',
  NO_PRODUCT_INCLUDED_IN_PRESCRIPTION: ':NO_PRODUCT_INCLUDED_IN_PRESCRIPTION',
} as const

type QualifierFunction = (claimRecord: ClaimRecord) => string | undefined

const hasPrescription = (claimRecord: ClaimRecord): boolean => {
  return Array.isArray(claimRecord?.prescriptions) && claimRecord.prescriptions.length > 0
}

const hasReferrer = (claimRecord: ClaimRecord): boolean => {
  const referrer = claimRecord?.claimData?.referrer
  return Boolean(referrer?.type) && Boolean(referrer?.firstName) && Boolean(referrer?.lastName)
}

const noProductIncludedInPrescription = (claimRecord: ClaimRecord): boolean => {
  const prescriptions = claimRecord?.prescriptions ?? []
  const products = claimRecord?.eligibleProducts ?? []
  return (
    prescriptions.length > 0 &&
    products.length > 0 &&
    products.filter(product => product.includedInPrescription).length === 0
  )
}

// qualifiers are applicable to specific claim record statuses
const QUALIFIER_MAP = {
  PRESCRIPTIONS_REQUIRED: {
    getQualifier: claimRecord => {
      if (hasReferrer(claimRecord)) {
        // check if rx validation is in progress
        const isSentForOCR =
          claimRecord?.prescriptions?.find(p => p.status === PrescriptionFileStatus.SENT_FOR_OCR) !== undefined
        if (isSentForOCR) {
          return QUALIFIERS.VALIDATE_PRESCRIPTION_IN_PROGRESS
        }

        return hasPrescription(claimRecord)
          ? QUALIFIERS.HAS_REFERRER_WITH_PRESCRIPTION
          : QUALIFIERS.HAS_REFERRER_NO_PRESCRIPTION
      }
    },
  },

  PRESCRIPTIONS_FOR_REVIEW: {
    getQualifier: claimRecord => {
      if (noProductIncludedInPrescription(claimRecord)) {
        return QUALIFIERS.NO_PRODUCT_INCLUDED_IN_PRESCRIPTION
      }
    },
  },

  READY_TO_CLAIM: {
    getQualifier: claimRecord => {
      if (noProductIncludedInPrescription(claimRecord)) {
        return QUALIFIERS.NO_PRODUCT_INCLUDED_IN_PRESCRIPTION
      }
    },
  },
} as Record<
  | ClaimRecordStatus.PRESCRIPTIONS_REQUIRED
  | ClaimRecordStatus.READY_TO_CLAIM
  | ClaimRecordStatus.PRESCRIPTIONS_FOR_REVIEW,
  { getQualifier: QualifierFunction }
>

const getStatusWithQualifier = (claimRecord: ClaimRecord): string => {
  const qualifierMap = QUALIFIER_MAP[claimRecord.status]
  if (typeof qualifierMap?.getQualifier !== 'function') return claimRecord.status

  const qualifier = qualifierMap.getQualifier(claimRecord) ?? ''
  return `${claimRecord?.status}${qualifier}`
}

/**
 * @returns the view to navigate to on resuming the insurance flow.
 * Applies to CA and VD flows
 */
export const getResumeToView = (claimRecord: ClaimRecord): ViewName | undefined => {
  const mapping = config.getFeatures()?.viewNavigation?.resumeMapping

  if (!mapping) {
    return undefined
  }

  // Note: explicit mapping of claim record status to view takes highest precedence
  let view = mapping[getStatusWithQualifier(claimRecord)]

  if (!view) {
    // default * mapping
    view = mapping['*']
  }

  return view
}
